import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { api } from "../lib/api";
import { COUNTY_SHAPES, MAP_VIEWBOX } from "../data/kenyaCounties";

const key = (n) => (n || "").trim().toLowerCase();

/**
 * Every plot and proposal the public has filed, on one map. Colour is activity, the number in the disc is the
 * count, and the counties with nothing on them are the point of the picture.
 */
export const LandLeadMap = () => {
  const [data, setData] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    api.get("/apply/map").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const counts = data.counts || {};
  const most = Math.max(1, ...Object.values(counts));
  const empty = COUNTY_SHAPES.map((s) => s.name).filter((n) => !counts[n]);

  return (
    <section className="mt-20" data-testid="land-lead-map">
      <p className="eyebrow">The pin map</p>
      <h2 className="display-3 mt-4">Where The Land Has Been Offered</h2>
      <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
        Every plot filed from this page and every proposal filed through the application, county by county, with
        nothing that identifies the person who filed it. The empty counties are the whole point: land nobody has
        put forward yet is where a first mover has no competition.
      </p>

      <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-3" data-testid="land-lead-map-stats">
        {[["Plots and proposals filed", data.total], ["Counties with a pin", `${data.counties_pinned} of 47`],
          ["Counties still open", 47 - data.counties_pinned]].map(([k, v], i) => (
          <div key={k} className="bg-[#0b0b0b] p-6" data-testid={`land-lead-stat-${i}`}>
            <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
            <p className="mono mt-3 text-lg text-white">{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <div className="relative border border-zinc-900 bg-[#070707] p-4">
          <svg viewBox={MAP_VIEWBOX} className="h-auto w-full" role="img" aria-label="Filed land by county">
            {COUNTY_SHAPES.map((s) => {
              const n = counts[s.name] || 0;
              const lit = n > 0;
              return (
                <path
                  key={s.code}
                  d={s.d}
                  fill={lit ? `rgba(229,169,30,${0.18 + (n / most) * 0.62})` : "#170606"}
                  stroke={key(hovered) === key(s.name) ? "#ffffff" : "#3a0f0b"}
                  strokeWidth={key(hovered) === key(s.name) ? 2.4 : 0.8}
                  className="cursor-pointer transition-colors duration-200"
                  onMouseEnter={() => setHovered(s.name)}
                  onMouseLeave={() => setHovered(null)}
                  data-testid={`land-lead-county-${key(s.name).replace(/[^a-z0-9]+/g, "-")}`}
                />
              );
            })}
            {COUNTY_SHAPES.filter((s) => (counts[s.name] || 0) > 0).map((s) => (
              <g key={`pin-${s.code}`} className="pointer-events-none">
                <circle cx={s.cx} cy={s.cy} r={13} fill="#E5A91E" stroke="#000" strokeWidth="1.5" />
                <text x={s.cx} y={s.cy + 5} textAnchor="middle" fontFamily="Oswald, sans-serif" fontSize="14"
                  fill="#0b0b0b" fontWeight="700">
                  {counts[s.name]}
                </text>
              </g>
            ))}
          </svg>
          {hovered ? (
            <p className="mono absolute left-6 top-6 border border-ochre/40 bg-black/85 px-3 py-2 text-[0.6rem] uppercase tracking-[0.16em] text-ochre"
              data-testid="land-lead-map-tip">
              {hovered} · {counts[hovered] || 0} filed
            </p>
          ) : null}
        </div>

        <div>
          <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">Counties with land filed</p>
          <div className="mt-4 space-y-px bg-zinc-800" data-testid="land-lead-ranked">
            {Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([name, n]) => (
              <div key={name} className="flex items-center justify-between bg-[#0b0b0b] px-4 py-3"
                data-testid={`land-lead-row-${key(name).replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="flex items-center gap-2 font-display text-sm uppercase text-white">
                  <MapPin size={12} className="text-ochre" /> {name}
                </span>
                <span className="mono text-xs text-ochre">{n}</span>
              </div>
            ))}
            {Object.keys(counts).length === 0 ? (
              <p className="mono bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-ochre"
                data-testid="land-lead-map-empty">
                Nothing filed yet. The first plot on this map is the one filed above.
              </p>
            ) : null}
          </div>
          <p className="mono mt-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400"
            data-testid="land-lead-open-counties">
            Still open: {empty.length} counties{empty.length ? ` — ${empty.slice(0, 10).join(", ")}` : ""}
            {empty.length > 10 ? " and more" : ""}
          </p>
        </div>
      </div>
    </section>
  );
};

export default LandLeadMap;
